import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Alert,
  Textarea,
  Select,
  SelectProps,
} from '@amzn/awsui-components-console';
import CronBuilder from './CronBuilder';
import { Schedule, ScheduleFrequencyType } from '../../types';
import { ScheduleFormData } from '../../hooks/useSchedules';

/**
 * Props for the CreateScheduleModal component
 */
export interface CreateScheduleModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (schedule: ScheduleFormData) => Promise<void>;
  initialData?: Schedule; // For edit mode
  jobId: string;
}

/**
 * Form validation errors interface
 */
interface FormErrors {
  scheduleName?: string;
  scheduleExpression?: string;
  timezone?: string;
}

/**
 * Timezone is fixed to UTC - all schedules run in UTC
 */
const UTC_TIMEZONE_OPTION: SelectProps.Option = { label: 'UTC (Coordinated Universal Time)', value: 'UTC' };

/**
 * CreateScheduleModal Component
 * 
 * A modal dialog for creating new schedules. Includes form fields for:
 * - Name (required)
 * - Description (optional)
 * - Schedule Expression (required, via CronBuilder) - supports cron() and rate() formats
 * - Frequency Type (CRON or RATE)
 * - Timezone (defaults to UTC)
 * 
 * Aligned with AWS EventBridge Scheduler and backend Smithy model.
 * 
 * Requirements: 2.1, 2.2, 2.5, 2.6, 2.7
 */
const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  initialData,
  jobId,
}) => {
  // Form state - aligned with backend schema
  const [scheduleName, setScheduleName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleExpression, setScheduleExpression] = useState('cron(0 12 * * ? *)'); // Default: daily at noon
  const [frequencyType, setFrequencyType] = useState<ScheduleFrequencyType>('CRON');
  const [timezone] = useState<SelectProps.Option>(UTC_TIMEZONE_OPTION);
  
  // Validation state
  const [errors, setErrors] = useState<FormErrors>({});
  const [cronValid, setCronValid] = useState(true);
  const [cronError, setCronError] = useState<string | undefined>();
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Determine if we're in edit mode
  const isEditMode = Boolean(initialData);
  const modalTitle = isEditMode ? 'Edit Schedule' : 'Create Schedule';

  /**
   * Extract cron expression from full schedule expression
   * e.g., "cron(0 12 * * ? *)" -> "0 12 * * ? *"
   */
  const extractCronExpression = (fullExpression: string): string => {
    const cronMatch = fullExpression.match(/^cron\((.+)\)$/);
    if (cronMatch) {
      return cronMatch[1];
    }
    return fullExpression;
  };

  /**
   * Wrap cron expression in cron() format
   */
  const wrapCronExpression = (expression: string): string => {
    if (expression.startsWith('cron(') || expression.startsWith('rate(')) {
      return expression;
    }
    return `cron(${expression})`;
  };

  /**
   * Reset form to initial state or populate with initialData
   */
  const resetForm = useCallback(() => {
    if (initialData) {
      // Edit mode: populate with existing data
      setScheduleName(initialData.scheduleName);
      setDescription(initialData.description || '');
      setScheduleExpression(initialData.scheduleExpression);
      setFrequencyType(initialData.frequencyType || 'CRON');
    } else {
      // Create mode: reset to defaults
      setScheduleName('');
      setDescription('');
      setScheduleExpression('cron(0 12 * * ? *)');
      setFrequencyType('CRON');
    }
    setErrors({});
    setCronValid(true);
    setCronError(undefined);
    setSubmitError(null);
  }, [initialData]);

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  /**
   * Handle cron expression change from CronBuilder
   */
  const handleCronChange = useCallback((newExpression: string) => {
    // Wrap in cron() format for backend
    setScheduleExpression(wrapCronExpression(newExpression));
    // Clear cron error when user makes changes
    setErrors((prev) => ({ ...prev, scheduleExpression: undefined }));
  }, []);

  /**
   * Handle cron validation change from CronBuilder
   */
  const handleCronValidationChange = useCallback((isValid: boolean, error?: string) => {
    setCronValid(isValid);
    setCronError(error);
  }, []);

  /**
   * Validate form fields
   * @returns true if form is valid, false otherwise
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation - required, 1-100 characters
    if (!scheduleName.trim()) {
      newErrors.scheduleName = 'Schedule name is required';
    } else if (scheduleName.trim().length > 100) {
      newErrors.scheduleName = 'Name must be 100 characters or less';
    }

    // Schedule expression validation
    if (!scheduleExpression.trim()) {
      newErrors.scheduleExpression = 'Schedule expression is required';
    } else if (!cronValid) {
      newErrors.scheduleExpression = cronError || 'Invalid schedule expression. Please use the builder or check syntax.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Clear previous submit error
    setSubmitError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formData: ScheduleFormData = {
        scheduleName: scheduleName.trim(),
        description: description.trim() || undefined,
        scheduleExpression: scheduleExpression.trim(),
        frequencyType: frequencyType,
        timezone: timezone.value || 'UTC',
      };

      await onSubmit(formData);
      
      // Success - close modal (parent component handles success notification)
      onDismiss();
    } catch (error: any) {
      // Keep modal open and display error
      console.error('Failed to submit schedule:', error);
      setSubmitError(error.message || 'Failed to save schedule. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle modal dismiss
   */
  const handleDismiss = () => {
    if (!isSubmitting) {
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close modal"
      size="large"
      header={modalTitle}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={handleDismiss}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!cronValid}
            >
              {isEditMode ? 'Save Changes' : 'Create Schedule'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {/* UTC Timezone Note - Requirement 2.5 */}
        <Alert type="info" header="Timezone Information">
          All schedule times are in <strong>UTC (Coordinated Universal Time)</strong>. 
          Please ensure you convert your local time to UTC when setting up schedules.
        </Alert>

        {/* Submit Error Display - Requirement 2.7 */}
        {submitError && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setSubmitError(null)}
            header="Error"
          >
            {submitError}
          </Alert>
        )}

        {/* Name Field - Requirement 2.2 */}
        <FormField
          label="Name"
          description="A unique name for this schedule"
          errorText={errors.scheduleName}
          constraintText="Required. Maximum 100 characters."
        >
          <Input
            value={scheduleName}
            onChange={({ detail }) => {
              setScheduleName(detail.value);
              setErrors((prev) => ({ ...prev, scheduleName: undefined }));
            }}
            placeholder="Enter schedule name"
            disabled={isSubmitting}
          />
        </FormField>

        {/* Description Field - Requirement 2.2 */}
        <FormField
          label={
            <span>
              Description <i>- optional</i>
            </span>
          }
          description="A brief description of what this schedule does"
        >
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            placeholder="Enter schedule description"
            rows={3}
            disabled={isSubmitting}
          />
        </FormField>

        {/* Schedule Expression Field - Requirement 2.2, 2.3, 2.4 */}
        <FormField
          label="Schedule"
          description="Define when this schedule should run"
          errorText={errors.scheduleExpression}
        >
          <CronBuilder
            value={extractCronExpression(scheduleExpression)}
            onChange={handleCronChange}
            onValidationChange={handleCronValidationChange}
          />
        </FormField>

        {/* Timezone Field - Fixed to UTC */}
        <FormField
          label="Timezone"
          description="All schedules are executed in UTC"
        >
          <Select
            selectedOption={timezone}
            options={[UTC_TIMEZONE_OPTION]}
            disabled={true}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default CreateScheduleModal;
